import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartShopping, faTruckFast } from '@fortawesome/free-solid-svg-icons';

import StockModal from './StockModal';
import { formatQty } from './stockShared';

/**
 * "I have bought these." One order, however many lines.
 *
 * ONE QUESTION PER LINE: HOW MANY. Everything else the record needs is already
 * known — who marked it, when, and the delivery estimate, which the API takes
 * from the catalogue when every line agrees on one. A reference is offered
 * because it is what ties this to the supplier's invoice, and it is optional.
 *
 * WHY ONE ORDER AND NOT ONE ROW EACH. A supplier run is a single purchase: one
 * delivery, one invoice, one set of photos when it lands. Recording it as five
 * unrelated rows is what made the invoice have to be filed five times.
 */
export default function OrderModal({ requests, onClose, onSubmit }) {
    const [quantities, setQuantities] = useState(() => {
        const init = {};
        requests.forEach((r) => { init[r.id] = r.quantity ? formatQty(r.quantity) : ''; });
        return init;
    });
    const [reference, setReference] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const single = requests.length === 1;
    const estimates = [...new Set(requests.map((r) => r.item_delivery_estimate || ''))];
    const sharedEta = estimates.length === 1 && estimates[0] ? estimates[0] : null;

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        setError('');
        try {
            await onSubmit({
                lines: requests.map((r) => ({
                    request_id: r.id,
                    quantity: quantities[r.id] === '' ? null : Number(quantities[r.id]),
                })),
                reference: reference.trim() || null,
            });
        } catch (err) {
            setError(err.response?.data?.error || 'That did not save. Try again.');
            setSaving(false);
        }
    };

    return (
        <StockModal
            title={single
                ? `Mark ordered — ${requests[0].item_name}`
                : `One order, ${requests.length} items`}
            subtitle="The warehouse is told straight away."
            onClose={onClose}
            width={520}
        >
            <form onSubmit={submit}>
                <span className="stk__fieldLabel">How many of each did you order?</span>
                <div className="stk__qtyLines">
                    {requests.map((r) => (
                        <label key={r.id} className="stk__qtyLine">
                            <span className="stk__qtyName">
                                {r.item_name}
                                <span className="stk__qtyCat">{r.category_name}</span>
                            </span>
                            <input
                                type="number"
                                className="stk__input stk__input--qty"
                                value={quantities[r.id]}
                                onChange={(e) => setQuantities((q) => ({ ...q, [r.id]: e.target.value }))}
                                placeholder="e.g. 4"
                                min="0"
                                step="any"
                                inputMode="decimal"
                            />
                        </label>
                    ))}
                </div>
                <span className="stk__fieldHint">
                    The warehouse sees these, so they know what to expect. Leave any blank if you are not sure yet.
                </span>

                <label className="stk__field stk__field--spaced">
                    <span className="stk__fieldLabel">Order reference (optional)</span>
                    <input
                        type="text"
                        className="stk__input"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="e.g. LM-4471"
                        maxLength={80}
                    />
                    <span className="stk__fieldHint">What the supplier calls this order — it ties to their invoice.</span>
                </label>

                {sharedEta ? (
                    <p className="stk__eta">
                        <FontAwesomeIcon icon={faTruckFast} />
                        Expected: <strong>{sharedEta}</strong>
                    </p>
                ) : null}

                {error ? <p className="stk__formError">{error}</p> : null}

                <div className="stk__modalActions">
                    <button type="button" className="stk__btn stk__btn--ghost" onClick={onClose}>Cancel</button>
                    <button type="submit" className="stk__btn stk__btn--primary" disabled={saving}>
                        <FontAwesomeIcon icon={faCartShopping} />{' '}
                        {saving ? 'Saving…' : (single ? 'Mark ordered' : `Place order (${requests.length})`)}
                    </button>
                </div>
            </form>
        </StockModal>
    );
}
